/**
 * Minimal Bootstrap-Vue → Vue 3 stub.
 * Provides skeleton components so old templates compile and render
 * without crashing until P1 component migration (BootstrapVue → PrimeVue 4).
 */
import { defineComponent, h, type App } from 'vue'

const pass = defineComponent({
  inheritAttrs: false,
  render() {
    return h('div', this.$attrs, this.$slots.default?.())
  },
})

const passSpan = defineComponent({
  inheritAttrs: false,
  render() {
    return h('span', this.$attrs, this.$slots.default?.())
  },
})

const passButton = defineComponent({
  inheritAttrs: false,
  emits: ['click'],
  render() {
    return h('button', { ...this.$attrs, type: 'button' }, this.$slots.default?.())
  },
})

const passInput = defineComponent({
  inheritAttrs: false,
  props: { modelValue: { default: '' } },
  emits: ['update:modelValue', 'input', 'change'],
  render() {
    return h('input', {
      ...this.$attrs,
      value: this.modelValue,
      onInput: (e: Event) => {
        this.$emit('update:modelValue', (e.target as HTMLInputElement).value)
        this.$emit('input', (e.target as HTMLInputElement).value)
      },
      onChange: (e: Event) => {
        this.$emit('change', (e.target as HTMLInputElement).value)
      },
    })
  },
})

const passCheckbox = defineComponent({
  inheritAttrs: false,
  props: { modelValue: { default: false }, value: { default: undefined } },
  emits: ['update:modelValue', 'change'],
  render() {
    return h('input', {
      ...this.$attrs,
      type: 'checkbox',
      checked: this.modelValue,
      onChange: (e: Event) => {
        const checked = (e.target as HTMLInputElement).checked
        this.$emit('update:modelValue', checked)
        this.$emit('change', checked)
      },
    })
  },
})

function formatCellValue(value: unknown): string {
  if (value === null || value === undefined) return ''
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value)
    } catch {
      return String(value)
    }
  }
  return String(value as string | number | boolean)
}

const BTable = defineComponent({
  inheritAttrs: false,
  props: {
    items: { default: () => [] },
    fields: { default: () => [] },
    busy: { default: false },
    perPage: { default: 10 },
    currentPage: { default: 1 },
  },
  emits: ['row-clicked', 'sort-changed'],
  render() {
    if (this.busy) {
      return h('div', { class: 'text-center' }, 'Loading...')
    }
    const fields = Array.isArray(this.fields)
      ? this.fields.map((f: unknown) =>
          typeof f === 'string' ? { key: f, label: f } : (f as { key: string; label: string }),
        )
      : []
    return h('table', { class: 'table', ...this.$attrs }, [
      h('thead', [h('tr', fields.map((f) => h('th', f.label ?? f.key)))]),
      h(
        'tbody',
        (this.items as Record<string, unknown>[]).map((row, i) =>
          h(
            'tr',
            {
              key: i,
              onClick: () => this.$emit('row-clicked', row),
              style: 'cursor:pointer',
            },
            fields.map((f) => h('td', formatCellValue(row[f.key]))),
          ),
        ),
      ),
    ])
  },
})

const BModal = defineComponent({
  inheritAttrs: false,
  props: {
    id: String,
    title: String,
    visible: Boolean,
    modelValue: Boolean,
    size: String,
    hideFooter: Boolean,
    hideHeader: Boolean,
    noCloseOnBackdrop: Boolean,
  },
  emits: ['update:modelValue', 'hidden', 'shown', 'ok', 'cancel', 'hide'],
  render() {
    const show = this.modelValue ?? this.visible
    if (!show) return h('div', { style: 'display:none' })
    return h(
      'div',
      { class: 'modal d-block', style: 'background:rgba(0,0,0,.5)', ...this.$attrs },
      [
        h('div', { class: ['modal-dialog', this.size && `modal-${this.size}`].filter(Boolean).join(' ') }, [
          h('div', { class: 'modal-content' }, [
            !this.hideHeader &&
              h('div', { class: 'modal-header' }, [
                h('h5', { class: 'modal-title' }, this.title),
                h(
                  'button',
                  {
                    class: 'close',
                    onClick: () => {
                      this.$emit('update:modelValue', false)
                      this.$emit('hidden')
                      this.$emit('hide')
                    },
                  },
                  '×',
                ),
              ]),
            h('div', { class: 'modal-body' }, this.$slots.default?.()),
            !this.hideFooter &&
              h('div', { class: 'modal-footer' }, [
                this.$slots['modal-footer']?.() ??
                  h(
                    'button',
                    {
                      class: 'btn btn-primary',
                      onClick: () => {
                        this.$emit('ok')
                        this.$emit('update:modelValue', false)
                      },
                    },
                    'OK',
                  ),
              ]),
          ]),
        ]),
      ],
    )
  },
})

const BPagination = defineComponent({
  props: {
    modelValue: { default: 1 },
    totalRows: { default: 0 },
    perPage: { default: 10 },
  },
  emits: ['update:modelValue', 'change'],
  render() {
    const pages = Math.ceil(this.totalRows / this.perPage)
    return h(
      'nav',
      h(
        'ul',
        { class: 'pagination' },
        Array.from({ length: pages }, (_, i) =>
          h('li', { class: `page-item${this.modelValue === i + 1 ? ' active' : ''}` }, [
            h(
              'a',
              {
                class: 'page-link',
                href: '#',
                onClick: (e: Event) => {
                  e.preventDefault()
                  this.$emit('update:modelValue', i + 1)
                  this.$emit('change', i + 1)
                },
              },
              String(i + 1),
            ),
          ]),
        ),
      ),
    )
  },
})

const BIcon = defineComponent({
  props: { icon: String },
  render() {
    return h('i', { class: `bi bi-${this.icon}`, ...this.$attrs })
  },
})

const COMPONENTS: Record<string, ReturnType<typeof defineComponent>> = {
  BButton: passButton,
  BButtonGroup: pass,
  BCard: pass,
  BCardText: pass,
  BCol: pass,
  BCollapse: pass,
  BDropdown: pass,
  BDropdownDivider: defineComponent({ render: () => h('hr') }),
  BDropdownItem: passButton,
  BForm: defineComponent({
    inheritAttrs: false,
    emits: ['submit'],
    render() {
      return h('form', { ...this.$attrs, onSubmit: (e: Event) => { e.preventDefault(); this.$emit('submit', e) } }, this.$slots.default?.())
    },
  }),
  BFormCheckbox: passCheckbox,
  BFormGroup: pass,
  BFormInput: passInput,
  BFormInvalidFeedback: passSpan,
  BFormRadio: defineComponent({
    inheritAttrs: false,
    props: { modelValue: { default: '' }, value: { default: '' } },
    emits: ['update:modelValue', 'change'],
    render() {
      return h('input', {
        ...this.$attrs,
        type: 'radio',
        checked: this.modelValue === this.value,
        onChange: () => {
          this.$emit('update:modelValue', this.value)
          this.$emit('change', this.value)
        },
      })
    },
  }),
  BFormRadioGroup: pass,
  BIcon: BIcon,
  BIconX: defineComponent({ render: () => h('span', '×') }),
  BInputGroup: pass,
  BInputGroupAppend: pass,
  BModal: BModal,
  BNavItem: defineComponent({
    inheritAttrs: false,
    props: { to: [String, Object] },
    render() {
      return h('li', { class: 'nav-item', ...this.$attrs }, [
        h('a', { class: 'nav-link', href: '#' }, this.$slots.default?.()),
      ])
    },
  }),
  BNavItemDropdown: pass,
  BNavbar: defineComponent({
    inheritAttrs: false,
    render() {
      return h('nav', { class: 'navbar', ...this.$attrs }, this.$slots.default?.())
    },
  }),
  BNavbarBrand: defineComponent({
    inheritAttrs: false,
    render() {
      return h('a', { class: 'navbar-brand', ...this.$attrs }, this.$slots.default?.())
    },
  }),
  BNavbarNav: defineComponent({
    inheritAttrs: false,
    render() {
      return h('ul', { class: 'navbar-nav', ...this.$attrs }, this.$slots.default?.())
    },
  }),
  BNavbarToggle: passButton,
  BPagination: BPagination,
  BRow: pass,
  BSpinner: defineComponent({
    render() {
      return h('div', { class: 'spinner-border', role: 'status' }, [
        h('span', { class: 'visually-hidden' }, 'Loading...'),
      ])
    },
  }),
  BTable: BTable,
}

const BootstrapVue = {
  install(app: App) {
    for (const [name, component] of Object.entries(COMPONENTS)) {
      app.component(name, component)
    }
    // Stub directives — no-ops prevent "unknown directive" Vue warnings
    app.directive('b-tooltip', {})
    app.directive('b-toggle', {
      mounted(el, binding) {
        el.addEventListener('click', () => {
          const targetId = typeof binding.value === 'string' ? binding.value : binding.arg
          if (targetId) {
            const target = document.getElementById(targetId)
            if (target) target.style.display = target.style.display === 'none' ? '' : 'none'
          }
        })
      },
    })
    app.directive('b-modal', {})
  },
}

const BootstrapVueIcons = {
  install(app: App) {
    app.component('BIcon', BIcon)
    app.component('BIconX', COMPONENTS['BIconX'])
  },
}

export { BootstrapVue, BootstrapVueIcons, COMPONENTS }
export default BootstrapVue
